import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, MenuItem, Typography } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiFormSelect from "src/components/core-components/mui/ByzMuiFormSelect";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import { referenceService } from "src/api/references/referenceService";
import { useAppSelector } from "src/app/hooks";
import { RootState } from "src/app/store";
import { WillOpenCardKey } from "src/api/request/requestService";
import { useTranslate } from "src/locales";

interface ContrastModalProps {
  open: boolean;
  onClose: () => void;
  serviceName?: string;
  onSave?: (data: { iscontrasted: string }) => void;
  // Merkezi additional-info fetch için opsiyonel parametreler
  additionalInfoKey?: WillOpenCardKey;
  additionalInfoParams?: {
    serviceId: number;
    requestDetailId: number;
    ptId?: number;
    officeId?: number;
  };
}

const ServiceRequestKonstrastModal: React.FC<ContrastModalProps> = ({
  open,
  onClose,
  serviceName = "Seçilen Hizmet Adı",
  onSave,
  additionalInfoKey,
  additionalInfoParams,
}) => {
  const { t } = useTranslate();
  const [selectedContrast, setSelectedContrast] = useState<string>("");
  const [additionalInfo, setAdditionalInfo] = useState<any>(null);
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const { selectedOfficeId } = useAppSelector((state: RootState) => ({
    selectedOfficeId: state.auth.selectedOfficeId,
  }));

  useEffect(() => {
    if (!open) return;

    const loadAdditionalInfo = async () => {
      try {
        // Backend sözleşmesine göre form tipi sabit "CONTRAST"
        const res = await referenceService.additionalInfo.getFields({
          formtype: "CONTRAST",
          offid:
            additionalInfoParams?.officeId ??
            (selectedOfficeId ? Number(selectedOfficeId) : undefined),
        });
        setAdditionalInfo(res);

        const field = res.fields && res.fields.length > 0 ? res.fields[0] : null;
        if (field && Array.isArray(field.options)) {
          const mapped = field.options.map((opt) => ({
            value: String(opt.value),
            label: opt.label,
          }));
          setOptions(mapped);
        } else {
          setOptions([
            { value: "0", label: "Kontrastsız" },
            { value: "1", label: "Kontrastlı - İlaç Kurumdan" },
            { value: "2", label: "Kontrastlı - İlaç Hastadan" },
          ]);
        }
      } catch {
        // Hata durumunda sabit listeye düş
        setOptions([
          { value: "0", label: "Kontrastsız" },
          { value: "1", label: "Kontrastlı - İlaç Kurumdan" },
          { value: "2", label: "Kontrastlı - İlaç Hastadan" },
        ]);
      }
    };

    loadAdditionalInfo();
  }, [open, additionalInfoParams, selectedOfficeId]);

  const handleSave = () => {
    const payload = { iscontrasted: selectedContrast || "1" };
    onSave?.(payload);
    if (!onSave) {
      onClose();
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          mx: -2,
          mt: 4,
          mb: 2,
          py: 1.5,
          px: 2,
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" fontWeight={700} color="#111827">
          {t("patient-request.contrast.title", { service: serviceName } as any)}
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.contrast.label")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzSelectListPopupInput
              fullWidth
              size="small"
              label={t("patient-request.contrast.label")}
              value={
                options.find((opt) => opt.value === selectedContrast)?.label ||
                ""
              }
              selectedId={selectedContrast || undefined}
              dialogMaxHeight="60vh"
              staticData={options.map((opt) => ({
                id: opt.value,
                NAME: opt.label,
              }))}
              displayField="NAME"
              onChange={(rows) => {
                const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                const first = list[0];
                if (first) {
                  setSelectedContrast(String(first.id));
                }
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mt: 3 }} />
      </Box>
    </OpenComponentDialog>
  );
};

export default ServiceRequestKonstrastModal;
